import { GeocodedAddress } from '../types/api';

/**
 * Что стало с адресом, когда партнёр поставил точку на карте.
 *
 * - `house` — по точке нашёлся дом, адрес заменён найденным;
 * - `street` — нашлась только улица, ею заполнено поле: нужен номер дома;
 * - `kept` — адрес оставлен, найденное — в `found`. `typed` — адрес вписал
 *   партнёр; иначе это подстановка по прошлой точке;
 * - `none` — по точке ничего не нашлось, адрес оставлен.
 */
export type AddressByPoint =
  | { status: 'house'; address: string }
  | { status: 'street'; address: string }
  | {
      status: 'kept';
      address: string;
      found: string;
      foundPrecision: GeocodedAddress['precision'];
      typed: boolean;
    }
  | { status: 'none'; address: string };

export interface AddressByPointOptions {
  /** Короче адрес не пройдёт проверку сервера. */
  minLength: number;
  /**
   * Адрес, который форма сама подставила по прошлой точке. Пока поле равно
   * ему, партнёр адрес не вводил — его можно заменить и улицей.
   */
  autoFilled?: string | null;
}

function normalize(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

/** Улица — первая часть подписи сервера: «улица Кенесары, 40, Астана». */
function streetOf(address: string): string {
  return normalize(address.split(',')[0] ?? '');
}

/** Номер дома — вторая часть подписи сервера с точностью `house`. */
function houseOf(label: string): string | null {
  const part = label.split(',')[1];
  return part ? normalize(part) : null;
}

/** Первый номер во введённом адресе: «12», «12а», «40/1». */
function firstNumberOf(address: string): string | null {
  const match = address.match(/\d+[^\s,]*/);
  return match ? normalize(match[0]) : null;
}

/**
 * Адрес после того, как партнёр поставил точку на карте (ADR-025).
 *
 * - Дом найден — адрес по точке заменяет поле. Кроме одного случая: партнёр
 *   сам вписал другой номер дома. Nominatim мог найти соседа, а свой номер
 *   партнёр знает лучше.
 * - Только улица заполняет пустое или слишком короткое поле — иначе
 *   «Кенесары 40», введённое руками, превратилось бы в «улица Кенесары» без
 *   номера. Адрес, подставленный по прошлой точке, улица заменяет, если это
 *   другая улица; на той же — номер дома остаётся: точку лишь подвинули.
 * - Район адресом не считается и поле не заполняет.
 *
 * То же правило — в мобильной анкете (`address_by_point.dart`).
 */
export function addressByPoint(
  current: string,
  found: GeocodedAddress | null,
  { minLength, autoFilled = null }: AddressByPointOptions,
): AddressByPoint {
  const label = found?.label.trim() ?? '';
  if (!found || label === '') {
    return { status: 'none', address: current };
  }

  const isUsable = current.trim().length >= minLength;
  const isAutoFilled = autoFilled !== null && current === autoFilled;
  const isTyped = isUsable && !isAutoFilled;
  const kept = (typed: boolean): AddressByPoint => ({
    status: 'kept',
    address: current,
    found: label,
    foundPrecision: found.precision,
    typed,
  });

  if (found.precision === 'house') {
    const typedNumber = isTyped ? firstNumberOf(current) : null;
    return typedNumber !== null && typedNumber !== houseOf(label) ? kept(true) : { status: 'house', address: label };
  }

  if (found.precision === 'street') {
    if (!isUsable) return { status: 'street', address: label };
    if (!isAutoFilled) return kept(true);
    return streetOf(current) === streetOf(label) ? kept(false) : { status: 'street', address: label };
  }

  return isUsable ? kept(isTyped) : { status: 'none', address: current };
}
